import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  FileText, 
  Users,
  BarChart3,
  PieChart,
  Loader2,
  Receipt,
  FileCheck
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnalyticsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const token = localStorage.getItem("cia_token");
      const response = await axios.get(`${API}/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      toast.error("Error al cargar analytics");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#5B9BD5]" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-600">No hay datos disponibles</p>
      </div>
    );
  }

  const growthPositive = data.current_month?.growth_percentage >= 0;

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Analytics</h1>
        <p className="text-slate-600">Visualiza el rendimiento de tu negocio</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="cia-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Ingresos del Mes</p>
                <p className="text-2xl font-bold text-slate-900">
                  {formatCurrency(data.current_month?.total || 0)}
                </p>
                <div className={`flex items-center gap-1 text-sm ${growthPositive ? 'text-green-600' : 'text-red-600'}`}>
                  {growthPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  <span>{data.current_month?.growth_percentage || 0}% vs mes anterior</span>
                </div>
              </div>
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cia-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Facturas Emitidas</p>
                <p className="text-2xl font-bold text-slate-900">{data.invoice_status?.issued || 0}</p>
                <p className="text-sm text-slate-500">{data.invoice_status?.draft || 0} borradores</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cia-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Ingresos Totales</p>
                <p className="text-2xl font-bold text-slate-900">
                  {formatCurrency(data.total_revenue || 0)}
                </p>
                <p className="text-sm text-slate-500">{data.total_invoices || 0} facturas</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cia-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Notas de Crédito</p>
                <p className="text-2xl font-bold text-slate-900">{data.total_credit_notes || 0}</p>
                <p className="text-sm text-red-500">-{formatCurrency(data.credit_notes_amount || 0)}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                <Receipt className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cia-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Complementos Pago</p>
                <p className="text-2xl font-bold text-slate-900">{data.total_payment_complements || 0}</p>
                <p className="text-sm text-green-500">{formatCurrency(data.payments_amount || 0)}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                <FileCheck className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cia-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Canceladas</p>
                <p className="text-2xl font-bold text-slate-900">{data.invoice_status?.cancelled || 0}</p>
                <p className="text-sm text-orange-500">{data.invoice_status?.cancellation_pending || 0} pendientes</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Resumen Financiero */}
      <Card className="cia-card">
        <CardHeader>
          <CardTitle>Resumen Financiero</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-700">Ingresos (Facturas)</p>
              <p className="text-xl font-bold text-green-800">{formatCurrency(data.total_revenue || 0)}</p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-red-700">Egresos (NC)</p>
              <p className="text-xl font-bold text-red-800">-{formatCurrency(data.credit_notes_amount || 0)}</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700">Cobrado (Complementos)</p>
              <p className="text-xl font-bold text-blue-800">{formatCurrency(data.payments_amount || 0)}</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-purple-700">Ingresos Netos</p>
              <p className="text-xl font-bold text-purple-800">
                {formatCurrency((data.total_revenue || 0) - (data.credit_notes_amount || 0))}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Revenue Chart */}
        <Card className="cia-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#5B9BD5]" />
              Ingresos Mensuales
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.monthly_revenue?.slice(-6).map((item, index) => {
                const maxValue = Math.max(...data.monthly_revenue.map(m => m.total));
                const percentage = maxValue > 0 ? (item.total / maxValue) * 100 : 0;
                
                return (
                  <div key={index} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">{item.month}</span>
                      <span className="font-medium">{formatCurrency(item.total)}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-[#5B9BD5] to-[#4A89C4] rounded-full transition-all"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Top Clients */}
        <Card className="cia-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#5B9BD5]" />
              Mejores Clientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {data.top_clients?.length > 0 ? (
                data.top_clients.map((client, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${
                        index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-slate-400' : index === 2 ? 'bg-amber-600' : 'bg-slate-300'
                      }`}>
                        {index + 1}
                      </div>
                      <span className="font-medium text-slate-900">{client.name}</span>
                    </div>
                    <span className="text-[#5B9BD5] font-semibold">{formatCurrency(client.revenue)}</span>
                  </div>
                ))
              ) : (
                <p className="text-center text-slate-500 py-4">Sin datos de clientes aún</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invoice Status Distribution */}
      <Card className="cia-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PieChart className="w-5 h-5 text-[#5B9BD5]" />
            Estado de Facturas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-6 justify-center">
            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2">
                <span className="text-2xl font-bold text-green-600">{data.invoice_status?.issued || 0}</span>
              </div>
              <p className="text-sm text-slate-600">Timbradas</p>
            </div>
            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-2">
                <span className="text-2xl font-bold text-yellow-600">{data.invoice_status?.draft || 0}</span>
              </div>
              <p className="text-sm text-slate-600">Borradores</p>
            </div>
            <div className="text-center">
              <div className="w-24 h-24 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-2">
                <span className="text-2xl font-bold text-red-600">{data.invoice_status?.cancelled || 0}</span>
              </div>
              <p className="text-sm text-slate-600">Canceladas</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsPage;
